// ============================================================================
// CLEO'S SECRET — Enrolment Success (Phase 3, UI slice 2b)
// File: src/pages/EnrolmentSuccess.jsx
//
// The closing page of the enrolment flow. Renders after a successful
// stripe.confirmPayment() + /api/program-plan-confirm round-trip.
//
// Two render entry paths:
//   1. Same-page card flow (the common path): PlanCheckout passes the full
//      plan + package + membership + scheduledPayments via routePayload.
//      No further server calls needed.
//   2. Off-site redirect-back path (Afterpay and similar BNPL): URL params
//      arrive as ?payment_intent=pi_...&payment_intent_client_secret=...
//      In that case we read the PI client secret from the URL, retrieve
//      the PI via Stripe to read its metadata (recommendation_id,
//      package_id, payment_option), pull customisations from localStorage
//      (or default empty), and POST to /api/program-plan-confirm. The
//      endpoint's idempotency means even if PlanCheckout's pre-redirect
//      call already wrote the plan, this is a safe replay that returns
//      the same plan.
//
// Brand-tone: calm, declarative, no fanfare. Receipt note quietly at the
// bottom. CTA labelled "Continue to your portal" — links to ClientPortal,
// which is the existing patient hub. When ProgramDashboard ships in
// Phase 4, swap the navigate target to 'portal' → 'program'.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  FF, P, T,
  Eyebrow,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';
import GraceCancelTrigger from '../components/GraceCancelTrigger';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const TIER_NAME = { silver: 'Silver', gold: 'Gold', black: 'Black' };
const LS_PREFIX = 'cleo:plan-customisations:';

// ─── Formatters ───
function formatDollars(cents) {
  if (cents == null) return '';
  return new Intl.NumberFormat('en-AU').format(Math.floor(cents / 100));
}
function formatAUDFull(cents) {
  if (cents == null) return '';
  return '$' + formatDollars(cents);
}
function shortDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function readCachedCustomisations(recommendationId) {
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + recommendationId);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) { return null; }
}

// ─── Diamond glyph ───
function Diamond({ size = 8, color, opacity = 1 }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      background: color || P.BR, transform: 'rotate(45deg)', opacity,
      flexShrink: 0,
    }} aria-hidden="true" />
  );
}

// ─── Calm CTA ───
function CalmCTA({ label, onClick, fullWidth = false }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        width: fullWidth ? '100%' : 'auto',
        padding: '16px 32px',
        background: hover ? 'rgba(201, 169, 97, 0.06)' : 'transparent',
        border: '1.5px solid ' + P.GO,
        color: P.BR2,
        fontFamily: FF,
        fontSize: 'clamp(17px, 1.4vw, 20px)',
        fontStyle: 'italic',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background 0.18s ease',
      }}
    >{label}</button>
  );
}

// ─── Schedule line item ───
function ScheduleLine({ label, sub, amount, isToday }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '14px 0',
      borderBottom: '1px solid rgba(200,184,136,0.35)',
    }}>
      <div>
        <div style={{ ...T.bodyMd, color: isToday ? P.INK : P.TX, fontWeight: isToday ? 600 : 500 }}>{label}</div>
        {sub && <div style={{ ...T.bodySmItalic, color: P.TL, marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ ...T.bodyMd, color: P.INK, fontWeight: 600 }}>{amount}</div>
    </div>
  );
}

// ─── Loading and error overlays ───
function LoadingOverlay({ message }) {
  return (
    <div style={{
      width: '100vw', minHeight: '100vh', background: P.SAND,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 24,
    }}>
      <Ankh size={48} color={P.GO} opacity={0.55} />
      <div style={{ ...T.bodyMdItalic, color: P.BR2 }}>{message}</div>
    </div>
  );
}

function PartialStateMessage({ onBack }) {
  return (
    <div style={{
      width: '100vw', minHeight: '100vh', background: P.SAND,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 'clamp(40px, 6vw, 96px) 24px',
    }}>
      <div style={{ maxWidth: 540, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <Ankh size={48} color={P.GO} opacity={0.55} />
        </div>
        <p style={{ ...T.premise, color: P.BR2, margin: '0 0 24px' }}>
          Setting up your plan is taking a little longer than expected.
        </p>
        <p style={{ ...T.bodyMd, color: P.TL, maxWidth: 480, margin: '0 auto 28px' }}>
          We've recorded your payment. Please contact us and we'll complete setup for you.
        </p>
        <CalmCTA label="Back to portal" onClick={onBack} />
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//                              MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════
export default function EnrolmentSuccess(props) {
  const {
    onNavigate,
    email, devEmail,
    plan: planFromProps,
    package: packageFromProps,
    services: servicesFromProps,
    scheduledPayments: scheduledPaymentsFromProps,
    membership: membershipFromProps,
  } = props;

  const effectiveEmail = email || devEmail;

  // Same-page path: data arrives via props.
  // Off-site redirect path: data must be retrieved from Stripe + confirm
  // endpoint using URL params. We detect the latter by the absence of
  // planFromProps AND the presence of a payment_intent URL param.
  const [data, setData] = useState(planFromProps ? {
    plan: planFromProps,
    package: packageFromProps,
    services: servicesFromProps,
    scheduledPayments: scheduledPaymentsFromProps,
    membership: membershipFromProps,
  } : null);
  const [loading, setLoading] = useState(!planFromProps);
  const [partialState, setPartialState] = useState(false);

  useEffect(function() {
    if (planFromProps) return; // same-page path — already populated
    let cancelled = false;

    async function recoverFromRedirect() {
      // Off-site redirect-back path. Read URL params Stripe appended.
      const params = new URLSearchParams(window.location.search);
      const clientSecret = params.get('payment_intent_client_secret');
      const paymentIntentId = params.get('payment_intent');
      if (!clientSecret || !paymentIntentId || !stripePromise || !effectiveEmail) {
        // Nothing to recover from — bounce to portal.
        if (onNavigate) onNavigate('portal');
        return;
      }

      try {
        const stripe = await stripePromise;
        const piRes = await stripe.retrievePaymentIntent(clientSecret);
        if (cancelled) return;
        const pi = piRes?.paymentIntent;
        if (!pi || pi.status !== 'succeeded') {
          // Payment didn't succeed — back to checkout to retry.
          if (onNavigate) onNavigate('plan-preview');
          return;
        }

        // Pull customisations from localStorage if present (PlanPreview
        // wrote them keyed by recommendationId before navigating).
        const recommendationId = pi.metadata?.recommendation_id;
        const cached = recommendationId ? readCachedCustomisations(recommendationId) : null;

        // Idempotent confirm — server will return existing plan if
        // PlanCheckout's pre-redirect attempt already wrote it.
        const res = await fetch('/api/program-plan-confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: effectiveEmail,
            paymentIntentId: pi.id,
            recommendationId: pi.metadata?.recommendation_id,
            packageId: pi.metadata?.package_id,
            paymentOption: pi.metadata?.payment_option,
            serviceEdits: cached?.serviceEdits || [],
            addedServices: cached?.addedServices || [],
            clientAttempt: 1,
          }),
        });
        const body = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setPartialState(true);
          setLoading(false);
          return;
        }
        setData(body);
        setLoading(false);

        // Strip URL params so a refresh doesn't re-trigger the recovery flow.
        window.history.replaceState({}, '', window.location.pathname);
      } catch (err) {
        if (!cancelled) {
          setPartialState(true);
          setLoading(false);
        }
      }
    }

    recoverFromRedirect();
    return function() { cancelled = true; };
  }, [planFromProps, effectiveEmail, onNavigate]);

  function handleContinue() {
    if (onNavigate) onNavigate('portal');
  }

  if (loading) return <LoadingOverlay message="Finalising your program..." />;
  if (partialState) return <PartialStateMessage onBack={handleContinue} />;
  if (!data?.plan) return <LoadingOverlay message="Loading your enrolment..." />;

  const { plan, package: pkg, scheduledPayments, membership } = data;
  const tier = TIER_NAME[membership?.tierSlug] || membership?.tierSlug || '';
  const isInstalment = plan.paymentOption === 'three_instalments';
  const sched = scheduledPayments || [];
  const totalPaid = plan.totalPaidCents || 0;

  return (
    <div style={{
      width: '100vw', maxWidth: '100vw', overflowX: 'hidden',
      minHeight: '100vh', background: P.SAND,
      paddingBottom: 60,
    }}>
      <main style={{
        maxWidth: 760, margin: '0 auto',
        padding: 'clamp(48px, 7vw, 96px) clamp(24px, 4vw, 40px)',
        textAlign: 'center',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <Ankh responsive="opener" color={P.GO} />
        </div>

        <div>
          <Eyebrow center>Your program is live</Eyebrow>
        </div>

        <h1 style={{
          ...T.display,
          fontSize: 'clamp(34px, 5vw, 64px)',
          color: P.INK,
          margin: '24px auto 18px',
          maxWidth: 720,
        }}>
          <span style={{ display: 'block' }}>Welcome to your</span>
          <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>
            12-week journey.
          </span>
        </h1>

        <p style={{
          ...T.premise,
          fontSize: 'clamp(17px, 1.6vw, 22px)',
          color: P.BR2,
          margin: '0 auto 32px',
          maxWidth: 600,
        }}>
          {pkg?.displayName ? pkg.displayName + ' begins now.' : 'Your program begins now.'} Week 1 unlocks today.
        </p>

        {/* Accent rule */}
        <div style={{
          width: 'clamp(180px, 22vw, 280px)',
          height: 2,
          background: P.GO,
          opacity: 0.85,
          margin: '0 auto 44px',
        }} />

        {/* Payment + membership panel — papyrus inset on sand */}
        <section style={{
          background: P.PAP,
          border: '1px solid ' + P.SAND3,
          padding: 'clamp(28px, 3vw, 40px)',
          textAlign: 'left',
          marginBottom: 36,
        }}>
          <div style={{ ...T.label, color: P.BR2, marginBottom: 18 }}>YOUR ENROLMENT</div>

          {tier && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
              <Diamond size={8} color={P.GO} opacity={0.7} />
              <span style={{ ...T.bodyMd, color: P.TX }}>
                <strong style={{ color: P.TL, fontWeight: 600 }}>{tier} membership</strong>
                <span style={{ color: P.TL }}> active</span>
              </span>
            </div>
          )}

          <div style={{ ...T.label, color: P.BR2, marginTop: 14, marginBottom: 6 }}>PAYMENT</div>

          {sched.length === 0 && (
            <ScheduleLine label="Paid in full" amount={formatAUDFull(totalPaid)} isToday />
          )}
          {sched.map(function(s, i) {
            const isFirst = i === 0;
            const isPaid = s.status === 'paid';
            const labelText = isFirst ? 'Today' : 'Week ' + s.targetWeek;
            const subText = isFirst
              ? (isPaid ? 'Paid' : null)
              : (isPaid ? 'Paid' : 'Around ' + shortDate(s.dueAt));
            return (
              <ScheduleLine
                key={s.id || i}
                label={labelText}
                sub={subText}
                amount={formatAUDFull(s.amountCents)}
                isToday={isFirst}
              />
            );
          })}

          {isInstalment && (
            <div style={{ ...T.bodySmItalic, color: P.TL, marginTop: 16 }}>
              Future instalments will be charged automatically to your saved card.
            </div>
          )}
        </section>

        {/* CTA */}
        <CalmCTA label="Continue to your portal" onClick={handleContinue} />

        {/* Receipt note */}
        <p style={{
          ...T.bodySmItalic,
          color: P.TL,
          marginTop: 36,
          maxWidth: 480,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          A receipt has been sent by Stripe to your email. You can cancel within 48 hours of enrolment for a full refund.
        </p>

        {/* Grace cancellation trigger — calm text link, fires shared modal */}
        <GraceCancelTrigger
          variant="link"
          plan={{
            id: plan.id,
            totalPaidCents: plan.totalPaidCents,
            paymentOption: plan.paymentOption,
          }}
          email={effectiveEmail}
          onCancelled={handleContinue}
        />
      </main>
    </div>
  );
}
